import { flow, getEnv, Instance, types } from "mobx-state-tree";

import { uploadFilesService, UploadStatusOption } from "../upload/UploadFilesService";
import { Column, ColumnType, RowItemData } from "../../components/datatable/interfaces";
import { translationService } from "../translation/TranslationService";
import { ApplicationError, errorHandler } from "../error-handler/ErrorHandlerService";
import { DataGridStorageModel } from "../../components/datatable/DataGridStorage";
import serializeToQueryParams from "../../helpers/QueryParams";
import { snackbarNotificationsService } from "../notifications/SnackbarNotificationsService";
import { endpoints } from "../api/ApiEndpoints";
import { InspectionFile } from "./InspectionFile";
import { FileExtended } from "../upload/FileUploadTracker";
import { FileTypesEnum } from "../../generated/Files";

const UploadInspectionFilesServiceModel = types
  .model({
    isUploadInProgress: types.boolean,
  })
  .views(() => ({
    get uploadFinished() {
      return uploadFilesService.uploadProgress.uploadFinished;
    },
    get isUploadOngoing() {
      return uploadFilesService.uploadStatus === UploadStatusOption.ongoing;
    },
  }))
  .actions((self) => ({
    updateMediaFile: flow(function* (
      file: FileExtended,
      inspectionId: number,
      mediaFileId: number
    ) {
      const endpoint = endpoints.get("upload_inspection_media_file");
      endpoint.routeParams = [inspectionId, mediaFileId];

      self.isUploadInProgress = true;
      yield getEnv(self).api.upload(endpoint, file.getFileFormData());
      self.isUploadInProgress = false;
    }),
    uploadMediaFiles: flow(function* (files: FileExtended[], inspectionId: number) {
      self.isUploadInProgress = true;
      yield uploadFilesService.fetchStorageRelatedData();
      uploadFilesService.addFilesForUpload(files);
      uploadFilesService.startInspectionUpload(inspectionId);
      self.isUploadInProgress = false;
    }),
  }));

const InspectionFilesFilter = types.model({
  sectionId: types.maybeNull(types.number),
  inspectionId: types.maybeNull(types.number),
  uploadService: UploadInspectionFilesServiceModel,
});

export const InspectionFilesStorageModel = types
  .compose(InspectionFilesFilter, DataGridStorageModel)
  .named("InspectionFilesStorageModel")
  .actions(() => {
    return {
      getTranslationKeyForType(type: FileTypesEnum) {
        if (type === FileTypesEnum.InspectionFile) return `api.file-type.InspectionFile`;
        return `general.file-type.${Object.keys(FileTypesEnum).find(
          (key) => (FileTypesEnum  as any)[key] === type
        )}`;
      },
    };
  })
  .views((self) => ({
    get tableColumns(): Column[] {
      return [
        {
          key: "name",
          name: "general.name",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
          flex: 2,
        },
        {
          key: "type",
          name: "general.inspections.files.list-header.type",
          type: ColumnType.SingleSelect,
          valueOptions: [
            {
              label: self.getTranslationKeyForType(FileTypesEnum.Document),
              value: FileTypesEnum.Document.toString(),
            },
            {
              label: self.getTranslationKeyForType(FileTypesEnum.Image),
              value: FileTypesEnum.Image.toString(),
            },
            {
              label: self.getTranslationKeyForType(FileTypesEnum.InspectionFile),
              value: FileTypesEnum.InspectionFile.toString(),
            },
            {
              label: self.getTranslationKeyForType(FileTypesEnum.Video),
              value: FileTypesEnum.Video.toString(),
            },
            {
              label: self.getTranslationKeyForType(FileTypesEnum.PanoramoFilm),
              value: FileTypesEnum.PanoramoFilm.toString(),
            },
          ],
          sortable: true,
          filterable: true,
        },
        {
          key: "contentType",
          name: "general.inspections.files.list-header.contentType",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "uploadedBy",
          name: "general.inspections.files.list-header.uploadedBy",
          type: ColumnType.String,
          sortable: true,
          filterable: true,
        },
        {
          key: "uploadedDate",
          name: "general.inspections.files.list-header.uploadedDate",
          type: ColumnType.DateTime,
          sortable: true,
          filterable: true,
        },
        {
          key: "size",
          name: "general.inspections.files.list-header.size",
          sortable: true,
        },
      ];
    },
  }))
  .actions((self) => {
    return {
      afterCreate() {
        self.defaultSort = { field: "name", sort: "asc" };
        self.gridColumns = self.tableColumns;
      },
      setSectionAndInspection(sectionId: number, inspectionId: number) {
        self.sectionId = sectionId;
        self.inspectionId = inspectionId;
      },
      mapItem(item: any) {
        return new InspectionFile(item);
      },

      deleteInspectionFiles: flow(function* deleteInspectionFiles(
        inspectionId: number,
        fileIds: number[]
      ) {
        const endpoint = endpoints.get("inspection_media_files");
        endpoint.routeParams = [inspectionId];

        const response = yield getEnv(self).api.delete(endpoint, fileIds);
        self.deselectAll();
        yield self.load();
        return response;
      }),

      convertVideo: flow(function* convertVideo(id: number) {
        try {
          const endpoint = endpoints.get("media_file_video_conversion");
          if (endpoint) endpoint.routeParams = [id];
          yield getEnv(self).api.put(endpoint);
        } catch (error) {
          const applicationError = error as ApplicationError;
          errorHandler.handleErrorInSnackbar(applicationError);
        }
      }),

      downloadInspectionFiles: flow(function* downloadInspectionFiles(
        fileIds: Array<number>,
        archiveName: string
      ) {
        try {
          const queryParams = serializeToQueryParams({ fileIds: fileIds && fileIds.toString() });
          const endpoint = endpoints.get("files_download");
          if (endpoint) endpoint.queryParams = queryParams;
          snackbarNotificationsService.showSuccess(
            translationService.translate("general.download.started")
          );
          yield getEnv(self).api.getDownloadToDisk(endpoint, `${archiveName}.zip`);
        } catch (error) {
          snackbarNotificationsService.showError(
            translationService.translate("default-error.download")
          );
        }
      }),

      downloadInspectionFile: flow(function* downloadInspectionFile(fileId: number, fileName) {
        try {
          const endpoint = endpoints.get("file_download");
          if (endpoint) endpoint.routeParams = [fileId];
          snackbarNotificationsService.showSuccess(
            translationService.translate("general.download.started")
          );
          yield getEnv(self).api.getDownloadToDisk(endpoint, fileName);
        } catch (error) {
          snackbarNotificationsService.showError(
            translationService.translate("default-error.download")
          );
        }
      }),

      validateIfFilesCanBeDeleted: flow(function* (inspectionId: number, fileIds: number[]) {
        const endpoint = endpoints.get("verify_files_can_be_deleted");
        endpoint.routeParams = [inspectionId];

        return yield getEnv(self).api.post(endpoint, fileIds);
      }),
    };
  })
  .views((self) => ({
    get endpoint() {
      const endpoint = endpoints.get("inspection_media_files_all");
      if (self.inspectionId) endpoint.routeParams = [self.inspectionId];

      endpoint.queryParams = self.getDataQuery();
      return endpoint;
    },
    get canLoadItems() {
      return !!self.sectionId || !!self.inspectionId;
    },
    getFileName(id: string) {
      if (self.data) {
        const items = self.data?.items as RowItemData[];
        return items.find((f) => f.id.toString() === id)?.displayValues.name.toDisplay;
      }
      return "";
    },
    isAnyFileOfTypeVideo() {
      var videoFileExists = false;
      if (self.data) {
        const items = self.data?.items as RowItemData[];

        self.selectedItems.forEach((fileId) => {
          if (
            items.find((f) => f.id === fileId)?.displayValues.type.toDisplay.toString() ===
            translationService.translate("general.video")
          )
            videoFileExists = true;
        });
      }
      return videoFileExists;
    },
    isAnalysisOrSegmentationSuccessfullyDoneForSelectedFiles(selectedItems: number[]): boolean {
      const video = self.data?.items.find(
        (item: InspectionFile) => item.fileType.id === FileTypesEnum.Video
      );

      if (!selectedItems.includes(video?.id.toString())) return false;

      return video?.isAnalysisSuccessfullyDone || video?.isSegmentationSuccessfullyDone;
    },
  }));

export type InspectionFilesStorage = Instance<typeof InspectionFilesStorageModel>;
